/**
 * Forge Demo Components — full interactive demos for modal
 * Extracted from ParagonLibraryPanel.tsx (TASK-009)
 */
/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect, useRef, useCallback } from 'react'
import StylistFlipCard from '@/components/cms/cards/StylistFlipCard'

/* ═══ INTERACTIVE DEMO COMPONENTS (for modal) ═══ */

function TestimonialCarouselDemo() {
  const [idx, setIdx] = useState(0)
  const reviews = [
    {
      stars: 5,
      text: 'Absolutely transformed my look. The attention to detail was incredible — I walked out feeling like a new person!',
      name: 'Sarah Mitchell',
      initials: 'SM',
      gradient: 'linear-gradient(135deg, #f472b6, #a78bfa)',
    },
    {
      stars: 5,
      text: "Best salon experience I've ever had. The stylist really listened to what I wanted and delivered beyond expectations.",
      name: 'Jessica Lee',
      initials: 'JL',
      gradient: 'linear-gradient(135deg, #60A5FA, #818cf8)',
    },
    {
      stars: 4,
      text: 'Love the atmosphere and the team is so talented. My color has never looked this vibrant. Already booked my next visit!',
      name: 'Maria Garcia',
      initials: 'MG',
      gradient: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    },
  ]
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 4000)
    return () => clearInterval(t)
  }, [reviews.length])
  const r = reviews[idx]
  return (
    <div style={{ width: 400, textAlign: 'center', color: '#fafafa' }}>
      <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginBottom: 16 }}>
        {Array.from({ length: r.stars }, (_, i) => (
          <span key={`star-on-${i + 1}`} style={{ color: '#f59e0b', fontSize: 20 }}>
            ★
          </span>
        ))}
        {Array.from({ length: 5 - r.stars }, (_, i) => (
          <span key={`star-off-${i + 1}`} style={{ color: '#3f3f46', fontSize: 20 }}>
            ★
          </span>
        ))}
      </div>
      <p
        style={{
          fontSize: 16,
          fontStyle: 'italic',
          color: '#d4d4d8',
          lineHeight: 1.6,
          marginBottom: 20,
          minHeight: 80,
          transition: 'opacity 0.3s',
        }}
      >
        &ldquo;{r.text}&rdquo;
      </p>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: r.gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          {r.initials}
        </div>
        <span style={{ fontSize: 14, color: '#a1a1aa' }}>{r.name}</span>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
        <button
          onClick={() => setIdx((idx - 1 + reviews.length) % reviews.length)}
          style={{
            background: 'none',
            border: '1px solid #3f3f46',
            borderRadius: '50%',
            width: 28,
            height: 28,
            color: '#a1a1aa',
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          ‹
        </button>
        {reviews.map((review, i) => (
          <button
            type="button"
            key={review.name}
            onClick={() => setIdx(i)}
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: i === idx ? '#f472b6' : 'rgba(255,255,255,0.15)',
              cursor: 'pointer',
              transition: 'background 0.2s',
              border: 'none',
              padding: 0,
            }}
          />
        ))}
        <button
          onClick={() => setIdx((idx + 1) % reviews.length)}
          style={{
            background: 'none',
            border: '1px solid #3f3f46',
            borderRadius: '50%',
            width: 28,
            height: 28,
            color: '#a1a1aa',
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          ›
        </button>
      </div>
    </div>
  )
}

function BeforeAfterDemo() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState(50)
  const [dragging, setDragging] = useState(false)
  const updatePos = useCallback((clientX: number) => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const pct = Math.max(5, Math.min(95, ((clientX - rect.left) / rect.width) * 100))
    setPos(pct)
  }, [])
  useEffect(() => {
    if (!dragging) return
    const onMove = (e: MouseEvent) => updatePos(e.clientX)
    const onUp = () => setDragging(false)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [dragging, updatePos])
  return (
    <div
      ref={containerRef}
      style={{
        width: 500,
        height: 300,
        position: 'relative',
        borderRadius: 12,
        overflow: 'hidden',
        cursor: 'ew-resize',
        userSelect: 'none',
      }}
      onMouseDown={(e) => {
        setDragging(true)
        updatePos(e.clientX)
      }}
      role="slider"
      tabIndex={0}
      aria-valuemin={5}
      aria-valuemax={95}
      aria-valuenow={Math.round(pos)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') setPos((prev) => Math.max(5, prev - 2))
        if (e.key === 'ArrowRight') setPos((prev) => Math.min(95, prev + 2))
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, #78350f, #451a03)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontSize: 48, opacity: 0.15 }}>💇</span>
        <span
          style={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            fontSize: 11,
            color: 'rgba(255,255,255,0.4)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 2,
          }}
        >
          Before
        </span>
      </div>
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: `${pos}%`,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #7c3aed, #db2777)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontSize: 48, opacity: 0.2 }}>✨</span>
        <span
          style={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            fontSize: 11,
            color: 'rgba(255,255,255,0.6)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 2,
          }}
        >
          After
        </span>
      </div>
      <div
        style={{
          position: 'absolute',
          left: `${pos}%`,
          top: 0,
          bottom: 0,
          width: 3,
          background: '#fff',
          transform: 'translateX(-50%)',
          zIndex: 2,
          boxShadow: '0 0 10px rgba(0,0,0,0.5)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            color: '#000',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          ⇔
        </div>
      </div>
    </div>
  )
}

function PricingTableDemo() {
  const [billing, setBilling] = useState<'monthly' | 'visit'>('monthly')
  const tiers = [
    {
      name: 'Starter',
      monthly: 39,
      visit: 45,
      features: ['Client portal', 'SEO tools', 'Up to 5 stylists'],
      accent: false,
    },
    {
      name: 'Pro',
      monthly: 99,
      visit: 95,
      features: ['Custom domain', 'Marketing automation', 'Advanced analytics'],
      accent: true,
    },
    {
      name: 'Enterprise',
      monthly: 249,
      visit: 189,
      features: [
        'White-label branding',
        'Full CMS editor',
        'Unlimited stylists',
        'Priority support',
        'All integrations',
      ],
      accent: false,
    },
  ]
  return (
    <div style={{ width: 560 }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
        <div
          style={{
            background: 'rgba(255,255,255,0.06)',
            borderRadius: 8,
            padding: 3,
            display: 'flex',
            gap: 3,
          }}
        >
          {(['monthly', 'visit'] as const).map((b) => (
            <button
              key={b}
              onClick={() => setBilling(b)}
              style={{
                padding: '6px 16px',
                borderRadius: 6,
                border: 'none',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s, color 0.2s',
                background: billing === b ? '#60A5FA' : 'transparent',
                color: billing === b ? '#000' : '#71717a',
              }}
            >
              {b === 'monthly' ? 'Monthly' : 'Per Visit'}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
        {tiers.map((t) => (
          <div
            key={t.name}
            style={{
              flex: 1,
              background: t.accent ? 'rgba(96,165,250,0.06)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${t.accent ? '#60A5FA' : '#27272a'}`,
              borderRadius: 12,
              padding: 20,
              transition: 'transform 0.2s, border-color 0.2s',
              position: 'relative',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
            }}
          >
            {t.accent && (
              <div
                style={{
                  position: 'absolute',
                  top: -10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#60A5FA',
                  color: '#000',
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '2px 10px',
                  borderRadius: 10,
                }}
              >
                POPULAR
              </div>
            )}
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: t.accent ? '#60A5FA' : '#d4d4d8',
                marginBottom: 8,
              }}
            >
              {t.name}
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: '#fafafa',
                fontFamily: "'JetBrains Mono', monospace",
                marginBottom: 4,
              }}
            >
              ${billing === 'monthly' ? t.monthly : t.visit}
            </div>
            <div style={{ fontSize: 11, color: '#71717a', marginBottom: 16 }}>
              /{billing === 'monthly' ? 'month' : 'visit'}
            </div>
            {t.features.map((f) => (
              <div
                key={f}
                style={{
                  fontSize: 12,
                  color: '#a1a1aa',
                  padding: '4px 0',
                  borderTop: '1px solid rgba(255,255,255,0.04)',
                }}
              >
                ✓ {f}
              </div>
            ))}
            <button
              style={{
                width: '100%',
                marginTop: 16,
                padding: '8px 0',
                borderRadius: 8,
                border: t.accent ? 'none' : '1px solid #3f3f46',
                background: t.accent ? '#60A5FA' : 'transparent',
                color: t.accent ? '#000' : '#a1a1aa',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Choose Plan
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

function BookingWidgetDemo() {
  const [selectedDay, setSelectedDay] = useState<number | null>(12)
  const [selectedSlot, setSelectedSlot] = useState<string | null>('10:30')
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const avail = [false, true, true, false, true, true, false]
  const slots = [
    '9:00',
    '9:30',
    '10:00',
    '10:30',
    '11:00',
    '11:30',
    '1:00',
    '1:30',
    '2:00',
    '2:30',
    '3:00',
    '3:30',
    '4:00',
    '4:30',
  ]
  return (
    <div style={{ width: 400, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#fafafa', textAlign: 'center' }}>
        February 2026
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {days.map((d) => (
          <div
            key={d}
            style={{ fontSize: 10, color: '#71717a', textAlign: 'center', paddingBottom: 4 }}
          >
            {d}
          </div>
        ))}
        {Array.from({ length: 28 }, (_, i) => {
          const day = i + 1
          const isAvail = avail[i % 7]
          const isSel = selectedDay === day
          return (
            <button
              type="button"
              key={`booking-day-${day}`}
              onClick={() => isAvail && setSelectedDay(day)}
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                cursor: isAvail ? 'pointer' : 'default',
                background: isSel
                  ? '#22c55e'
                  : isAvail
                    ? 'rgba(34,197,94,0.1)'
                    : 'rgba(255,255,255,0.02)',
                color: isSel ? '#000' : isAvail ? '#22c55e' : '#3f3f46',
                fontWeight: isSel ? 700 : 400,
                border: isSel ? '2px solid #22c55e' : '1px solid transparent',
                transition: 'background 0.15s, color 0.15s, border-color 0.15s',
              }}
            >
              {day}
            </button>
          )
        })}
      </div>
      {selectedDay && (
        <div>
          <div style={{ fontSize: 12, color: '#a1a1aa', marginBottom: 8 }}>
            Available slots for Feb {selectedDay}:
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {slots
              .filter((_, i) => avail[i % 7])
              .map((s) => (
                <button
                  key={s}
                  onClick={() => setSelectedSlot(s)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: selectedSlot === s ? '1px solid #22c55e' : '1px solid #27272a',
                    background:
                      selectedSlot === s ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.03)',
                    color: selectedSlot === s ? '#22c55e' : '#a1a1aa',
                    fontSize: 12,
                    cursor: 'pointer',
                    fontFamily: "'JetBrains Mono', monospace",
                    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
                  }}
                >
                  {s}
                </button>
              ))}
          </div>
        </div>
      )}
      {selectedDay && selectedSlot && (
        <button
          style={{
            padding: '10px 0',
            borderRadius: 8,
            border: 'none',
            background: '#22c55e',
            color: '#000',
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Book Feb {selectedDay} at {selectedSlot}
        </button>
      )}
    </div>
  )
}

function GalleryLightboxDemo() {
  const [expanded, setExpanded] = useState<number | null>(null)
  const images = [
    { gradient: 'linear-gradient(135deg, #a78bfa, #6366f1)', label: 'Balayage' },
    { gradient: 'linear-gradient(135deg, #f472b6, #ec4899)', label: 'Color Correction' },
    { gradient: 'linear-gradient(135deg, #60A5FA, #3b82f6)', label: 'Precision Cut' },
    { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', label: 'Highlights' },
  ]
  if (expanded !== null) {
    const img = images[expanded]
    return (
      <div style={{ width: 500 }}>
        <div
          style={{
            background: img.gradient,
            borderRadius: 12,
            height: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 12,
            position: 'relative',
          }}
        >
          <span style={{ fontSize: 48, opacity: 0.3 }}>🖼</span>
          <div
            style={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              fontSize: 16,
              fontWeight: 700,
              color: '#fff',
            }}
          >
            {img.label}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={() => setExpanded((expanded - 1 + images.length) % images.length)}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid #3f3f46',
              borderRadius: 8,
              padding: '6px 14px',
              color: '#a1a1aa',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            ← Prev
          </button>
          <span style={{ fontSize: 12, color: '#71717a' }}>
            {expanded + 1} / {images.length}
          </span>
          <button
            onClick={() => setExpanded((expanded + 1) % images.length)}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid #3f3f46',
              borderRadius: 8,
              padding: '6px 14px',
              color: '#a1a1aa',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Next →
          </button>
        </div>
        <button
          onClick={() => setExpanded(null)}
          style={{
            width: '100%',
            marginTop: 12,
            padding: '8px 0',
            borderRadius: 8,
            border: '1px solid #3f3f46',
            background: 'transparent',
            color: '#71717a',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Back to Grid
        </button>
      </div>
    )
  }
  return (
    <div style={{ width: 400 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {images.map((img, i) => (
          <button
            type="button"
            key={img.label}
            onClick={() => setExpanded(i)}
            style={{
              background: img.gradient,
              borderRadius: 10,
              height: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              position: 'relative',
              transition: 'transform 0.2s',
              border: 'none',
              padding: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.03)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            <span style={{ fontSize: 24, opacity: 0.3 }}>🖼</span>
            <div
              style={{
                position: 'absolute',
                bottom: 8,
                left: 10,
                fontSize: 11,
                fontWeight: 600,
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              {img.label}
            </div>
          </button>
        ))}
      </div>
      <div style={{ fontSize: 11, color: '#52525b', textAlign: 'center', marginTop: 10 }}>
        Click any image to expand
      </div>
    </div>
  )
}

/* ═══ NEW INTERACTIVE DEMO COMPONENTS ═══ */

function TeamRosterDemo() {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)
  const team = [
    {
      name: 'Jordan Lee',
      title: 'Senior Colorist',
      specialties: ['Balayage', 'Vivid Color'],
      avail: true,
      initials: 'JL',
      accent: '#14b8a6',
    },
    {
      name: 'Maya Chen',
      title: 'Master Stylist',
      specialties: ['Precision Cuts', 'Extensions'],
      avail: true,
      initials: 'MC',
      accent: '#f59e0b',
    },
    {
      name: 'Kai Nakamura',
      title: 'Texture Specialist',
      specialties: ['Curly Hair', 'Silk Press'],
      avail: false,
      initials: 'KN',
      accent: '#fb7185',
    },
    {
      name: 'River Osei',
      title: 'Creative Director',
      specialties: ['Editorial', 'Bridal'],
      avail: true,
      initials: 'RO',
      accent: '#818cf8',
    },
  ]
  return (
    <div style={{ width: 520, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {team.map((m, i) => (
        <div
          key={m.name}
          onMouseEnter={() => setHoveredIdx(i)}
          onMouseLeave={() => setHoveredIdx(null)}
          style={{
            background: hoveredIdx === i ? `${m.accent}10` : 'rgba(255,255,255,0.02)',
            border: `1px solid ${hoveredIdx === i ? m.accent : '#27272a'}`,
            borderRadius: 12,
            padding: 16,
            transition: 'background 0.25s, border-color 0.25s, transform 0.25s',
            transform: hoveredIdx === i ? 'translateY(-3px)' : 'none',
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: `linear-gradient(135deg, ${m.accent}, ${m.accent}88)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                fontWeight: 700,
                color: '#fff',
                flexShrink: 0,
              }}
            >
              {m.initials}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fafafa' }}>{m.name}</div>
              <div style={{ fontSize: 11, color: m.accent }}>{m.title}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
            {m.specialties.map((s) => (
              <span
                key={s}
                style={{
                  fontSize: 10,
                  padding: '2px 8px',
                  borderRadius: 4,
                  background: `${m.accent}15`,
                  color: m.accent,
                  fontWeight: 500,
                }}
              >
                {s}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: m.avail ? '#22c55e' : '#ef4444',
                }}
              />
              <span style={{ fontSize: 10, color: m.avail ? '#22c55e' : '#ef4444' }}>
                {m.avail ? 'Available' : 'Booked'}
              </span>
            </div>
            <button
              style={{
                fontSize: 10,
                padding: '4px 12px',
                borderRadius: 6,
                border: `1px solid ${m.accent}`,
                background: 'transparent',
                color: m.accent,
                cursor: 'pointer',
                fontWeight: 600,
                transition: 'background 0.2s, color 0.2s',
              }}
            >
              Book →
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

function ServiceMenuDemo() {
  const [openCat, setOpenCat] = useState<string>('cuts')
  const categories = [
    {
      id: 'cuts',
      name: 'Haircuts & Styling',
      items: [
        { n: "Women's Cut & Style", d: '45 min', p: '$65' },
        { n: "Men's Cut", d: '30 min', p: '$40' },
        { n: 'Kids Cut (12 & under)', d: '20 min', p: '$30' },
        { n: 'Blowout & Style', d: '35 min', p: '$50' },
      ],
    },
    {
      id: 'color',
      name: 'Color Services',
      items: [
        { n: 'Single Process Color', d: '90 min', p: '$120' },
        { n: 'Full Highlights', d: '120 min', p: '$185' },
        { n: 'Balayage / Ombré', d: '150 min', p: '$225' },
        { n: 'Color Correction', d: '180+ min', p: '$300+' },
      ],
    },
    {
      id: 'treat',
      name: 'Treatments',
      items: [
        { n: 'K18 Treatment', d: '30 min', p: '$75' },
        { n: 'Olaplex Bonding', d: '25 min', p: '$50' },
        { n: 'Scalp Detox', d: '20 min', p: '$45' },
      ],
    },
  ]
  return (
    <div style={{ width: 440 }}>
      {categories.map((cat) => (
        <div key={cat.id} style={{ marginBottom: 8 }}>
          <button
            onClick={() => setOpenCat(openCat === cat.id ? '' : cat.id)}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              background: openCat === cat.id ? 'var(--color-warning-bg)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${openCat === cat.id ? '#f59e0b' : '#27272a'}`,
              borderRadius: openCat === cat.id ? '10px 10px 0 0' : 10,
              color: openCat === cat.id ? '#f59e0b' : '#d4d4d8',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background 0.2s, border-color 0.2s, color 0.2s',
            }}
          >
            {cat.name}
            <span
              style={{
                fontSize: 12,
                transition: 'transform 0.2s',
                display: 'inline-block',
                transform: openCat === cat.id ? 'rotate(180deg)' : 'none',
              }}
            >
              ▾
            </span>
          </button>
          {openCat === cat.id && (
            <div
              style={{
                border: '1px solid #27272a',
                borderTop: 'none',
                borderRadius: '0 0 10px 10px',
                overflow: 'hidden',
              }}
            >
              {cat.items.map((item, i) => (
                <div
                  key={item.n}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 16px',
                    borderBottom: i < cat.items.length - 1 ? '1px solid #1e1e24' : 'none',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--color-warning-bg)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent'
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, color: '#fafafa', fontWeight: 500 }}>{item.n}</div>
                    <div style={{ fontSize: 11, color: '#71717a', marginTop: 2 }}>{item.d}</div>
                  </div>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#f59e0b',
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                  >
                    {item.p}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function LoyaltyRewardsDemo() {
  const [visits] = useState(7)
  const tiers = [
    { name: 'Bronze', min: 0, color: '#cd7f32' },
    { name: 'Silver', min: 5, color: '#a8a8a8' },
    { name: 'Gold', min: 10, color: '#f59e0b' },
    { name: 'Platinum', min: 15, color: '#e879f9' },
  ]
  const currentTier = tiers.filter((t) => visits >= t.min).pop()!
  const nextTier = tiers.find((t) => t.min > visits)
  const progress = nextTier
    ? ((visits - currentTier.min) / (nextTier.min - currentTier.min)) * 100
    : 100
  const circumference = 2 * Math.PI * 80
  return (
    <div style={{ width: 360, textAlign: 'center', color: '#fafafa' }}>
      <svg
        width="200"
        height="200"
        viewBox="0 0 200 200"
        style={{ margin: '0 auto 20px', display: 'block' }}
      >
        <circle cx="100" cy="100" r="80" stroke="#1e1e24" strokeWidth="8" fill="none" />
        <circle
          cx="100"
          cy="100"
          r="80"
          stroke={currentTier.color}
          strokeWidth="8"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - progress / 100)}
          strokeLinecap="round"
          transform="rotate(-90 100 100)"
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
        <text x="100" y="90" textAnchor="middle" fill="#fafafa" fontSize="32" fontWeight="700">
          {visits}
        </text>
        <text x="100" y="115" textAnchor="middle" fill="#71717a" fontSize="13">
          visits
        </text>
      </svg>
      <div style={{ fontSize: 18, fontWeight: 700, color: currentTier.color, marginBottom: 4 }}>
        {currentTier.name} Tier
      </div>
      {nextTier && (
        <div style={{ fontSize: 13, color: '#71717a', marginBottom: 20 }}>
          {nextTier.min - visits} more visits to {nextTier.name}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
        {tiers.map((t) => (
          <div key={t.name} style={{ textAlign: 'center', opacity: visits >= t.min ? 1 : 0.3 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: visits >= t.min ? t.color : '#27272a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                margin: '0 auto 4px',
                border: visits >= t.min ? `2px solid ${t.color}` : '2px solid #27272a',
              }}
            >
              ✦
            </div>
            <div style={{ fontSize: 10, color: visits >= t.min ? t.color : '#3f3f46' }}>
              {t.name}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SocialProofDemo() {
  const [likedPosts, setLikedPosts] = useState<Set<number>>(new Set())
  const posts = [
    { gradient: 'linear-gradient(135deg, #f97316, #dc2626)', likes: 47, label: 'Balayage Reveal' },
    {
      gradient: 'linear-gradient(135deg, #14b8a6, #0d9488)',
      likes: 92,
      label: 'Curl Transformation',
    },
    { gradient: 'linear-gradient(135deg, #f472b6, #a855f7)', likes: 156, label: 'Bridal Updo' },
    { gradient: 'linear-gradient(135deg, #60A5FA, #2563eb)', likes: 31, label: "Men's Fade" },
    { gradient: 'linear-gradient(135deg, #f59e0b, #ea580c)', likes: 78, label: 'Vivid Color' },
    { gradient: 'linear-gradient(135deg, #a78bfa, #7c3aed)', likes: 210, label: 'Before & After' },
  ]
  return (
    <div style={{ width: 440 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        {posts.map((p, i) => (
          <div
            key={p.label}
            style={{
              background: p.gradient,
              borderRadius: 10,
              height: 130,
              position: 'relative',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              overflow: 'hidden',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.04)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                opacity: 0.2,
              }}
            >
              📸
            </div>
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '24px 8px 8px',
                background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 600, color: '#fff', marginBottom: 4 }}>
                {p.label}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setLikedPosts((prev) => {
                    const n = new Set(prev)
                    if (n.has(i)) {
                      n.delete(i)
                    } else {
                      n.add(i)
                    }
                    return n
                  })
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 11,
                  color: likedPosts.has(i) ? '#ef4444' : 'rgba(255,255,255,0.7)',
                  cursor: 'pointer',
                  padding: 0,
                  fontWeight: 600,
                }}
              >
                {likedPosts.has(i) ? '♥' : '♡'} {p.likes + (likedPosts.has(i) ? 1 : 0)}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function HoursLocationDemo() {
  const dayOfWeek = new Date().getDay()
  const days = [
    { name: 'Monday', hours: '9:00 AM – 7:00 PM' },
    { name: 'Tuesday', hours: '9:00 AM – 7:00 PM' },
    { name: 'Wednesday', hours: '9:00 AM – 8:00 PM' },
    { name: 'Thursday', hours: '9:00 AM – 7:00 PM' },
    { name: 'Friday', hours: '9:00 AM – 8:00 PM' },
    { name: 'Saturday', hours: '9:00 AM – 6:00 PM' },
    { name: 'Sunday', hours: 'Closed' },
  ]
  const todayIdx = dayOfWeek === 0 ? 6 : dayOfWeek - 1
  return (
    <div style={{ width: 400, display: 'flex', gap: 16 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#fafafa', marginBottom: 12 }}>
          Business Hours
        </div>
        {days.map((d, i) => (
          <div
            key={d.name}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 10px',
              borderLeft: i === todayIdx ? '3px solid #38bdf8' : '3px solid transparent',
              background: i === todayIdx ? 'rgba(56,189,248,0.06)' : 'transparent',
              borderRadius: i === todayIdx ? '0 6px 6px 0' : 0,
              marginBottom: 2,
              transition: 'background 0.2s, border-color 0.2s, color 0.2s',
            }}
          >
            <span
              style={{
                fontSize: 13,
                color: i === todayIdx ? '#38bdf8' : '#a1a1aa',
                fontWeight: i === todayIdx ? 700 : 400,
              }}
            >
              {d.name} {i === todayIdx && '· Today'}
            </span>
            <span
              style={{
                fontSize: 13,
                color: d.hours === 'Closed' ? '#ef4444' : i === todayIdx ? '#fafafa' : '#71717a',
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: i === todayIdx ? 600 : 400,
              }}
            >
              {d.hours}
            </span>
          </div>
        ))}
      </div>
      <div style={{ width: 160, flexShrink: 0 }}>
        <div
          style={{
            background: 'linear-gradient(135deg, #0f1a2a, #1a2540)',
            borderRadius: 12,
            height: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            border: '1px solid #27272a',
            marginBottom: 10,
          }}
        >
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: '#38bdf8',
              boxShadow: '0 0 12px #38bdf8',
              animation: 'pulse 2s ease infinite',
            }}
          />
          <div style={{ position: 'absolute', bottom: 8, fontSize: 9, color: '#52525b' }}>
            📍 Downtown
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#22c55e',
              boxShadow: '0 0 6px rgba(34,197,94,0.4)',
            }}
          />
          <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>Open Now</span>
        </div>
        <div style={{ fontSize: 11, color: '#71717a' }}>123 Main St, Suite 200</div>
        <div style={{ fontSize: 11, color: '#71717a' }}>(555) 123-4567</div>
      </div>
    </div>
  )
}

function ProductShowcaseDemo() {
  const [cart, setCart] = useState<Set<number>>(new Set())
  const products = [
    { name: 'Olaplex No.3', brand: 'Olaplex', price: 30, desc: 'Hair Perfector', color: '#34d399' },
    {
      name: 'K18 Leave-In Mask',
      brand: 'K18',
      price: 75,
      desc: 'Molecular Repair',
      color: '#60A5FA',
    },
    {
      name: 'Moroccanoil Treatment',
      brand: 'Moroccanoil',
      price: 48,
      desc: 'Argan Oil Infused',
      color: '#f59e0b',
    },
    {
      name: 'Oribe Gold Lust',
      brand: 'Oribe',
      price: 52,
      desc: 'Repair & Restore',
      color: '#f472b6',
    },
  ]
  return (
    <div style={{ width: 560 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Featured Products</div>
        <span style={{ fontSize: 12, color: '#34d399', fontWeight: 600 }}>{cart.size} in bag</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        {products.map((p, i) => (
          <div
            key={p.name}
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${cart.has(i) ? p.color : '#27272a'}`,
              borderRadius: 12,
              padding: 14,
              textAlign: 'center',
              transition: 'border-color 0.25s, transform 0.25s',
              transform: cart.has(i) ? 'translateY(-2px)' : 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = p.color
            }}
            onMouseLeave={(e) => {
              if (!cart.has(i)) e.currentTarget.style.borderColor = '#27272a'
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 10,
                background: `${p.color}15`,
                margin: '0 auto 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
              }}
            >
              🧴
            </div>
            <div style={{ fontSize: 10, color: '#71717a', marginBottom: 2 }}>{p.brand}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#fafafa', marginBottom: 2 }}>
              {p.name}
            </div>
            <div style={{ fontSize: 10, color: '#52525b', marginBottom: 8 }}>{p.desc}</div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: p.color,
                fontFamily: "'JetBrains Mono', monospace",
                marginBottom: 10,
              }}
            >
              ${p.price}
            </div>
            <button
              onClick={() =>
                setCart((prev) => {
                  const n = new Set(prev)
                  if (n.has(i)) {
                    n.delete(i)
                  } else {
                    n.add(i)
                  }
                  return n
                })
              }
              style={{
                width: '100%',
                padding: '6px 0',
                borderRadius: 6,
                border: `1px solid ${p.color}`,
                background: cart.has(i) ? p.color : 'transparent',
                color: cart.has(i) ? '#000' : p.color,
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s, color 0.2s',
              }}
            >
              {cart.has(i) ? '✓ Added' : 'Add to Bag'}
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

function AppointmentStepsDemo() {
  const [activeStep, setActiveStep] = useState(1)
  const steps = [
    {
      title: 'Check In',
      desc: 'Arrive & confirm your appointment at the front desk. Enjoy complimentary beverages while you wait.',
      icon: '📋',
    },
    {
      title: 'Consultation',
      desc: 'Meet your stylist to discuss your vision, review inspiration photos, and plan your service.',
      icon: '💬',
    },
    {
      title: 'Service',
      desc: 'Relax while your stylist works their magic. Enjoy our premium products and techniques.',
      icon: '✂️',
    },
    {
      title: 'Finishing',
      desc: 'Final styling, product recommendations, and a reveal of your new look.',
      icon: '✨',
    },
    {
      title: 'Review',
      desc: 'Share your experience, book your next visit, and earn loyalty points.',
      icon: '⭐',
    },
  ]
  return (
    <div style={{ width: 480 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 24,
          gap: 0,
        }}
      >
        {steps.map((s, i) => (
          <div key={s.title} style={{ display: 'flex', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => setActiveStep(i)}
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: i <= activeStep ? '#818cf8' : '#1e1e24',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                cursor: 'pointer',
                border: i === activeStep ? '2px solid #c4b5fd' : '2px solid transparent',
                transition: 'background 0.2s, border-color 0.2s, box-shadow 0.2s',
                boxShadow: i === activeStep ? '0 0 12px rgba(129,140,248,0.3)' : 'none',
                padding: 0,
              }}
            >
              {s.icon}
            </button>
            {i < steps.length - 1 && (
              <div
                style={{
                  width: 40,
                  height: 2,
                  background: i < activeStep ? '#818cf8' : '#1e1e24',
                  transition: 'background 0.3s',
                }}
              />
            )}
          </div>
        ))}
      </div>
      <div
        style={{
          background: 'rgba(129,140,248,0.06)',
          border: '1px solid rgba(129,140,248,0.2)',
          borderRadius: 12,
          padding: 20,
          textAlign: 'center',
          minHeight: 100,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, color: '#818cf8', marginBottom: 4 }}>
          {steps[activeStep].title}
        </div>
        <p
          style={{
            fontSize: 14,
            color: '#a1a1aa',
            lineHeight: 1.6,
            maxWidth: 380,
            margin: '0 auto',
          }}
        >
          {steps[activeStep].desc}
        </p>
        <div style={{ fontSize: 11, color: '#52525b', marginTop: 12 }}>
          Step {activeStep + 1} of {steps.length}
        </div>
      </div>
    </div>
  )
}

function ContactFormDemo() {
  const [step, setStep] = useState(0)
  const [formData, setFormData] = useState({ name: '', email: '', service: '', message: '' })
  const totalSteps = 3
  return (
    <div style={{ width: 400 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {Array.from({ length: totalSteps }, (_, i) => (
          <div
            key={`contact-step-${i + 1}`}
            style={{
              flex: 1,
              height: 3,
              borderRadius: 2,
              background: i <= step ? '#e879f9' : '#27272a',
              transition: 'background 0.3s',
            }}
          />
        ))}
      </div>
      {step === 0 && (
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fafafa', marginBottom: 16 }}>
            Your Details
          </div>
          {['name', 'email'].map((field) => (
            <div key={field} style={{ marginBottom: 12 }}>
              <label
                style={{
                  fontSize: 11,
                  color: '#71717a',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'block',
                  marginBottom: 4,
                }}
              >
                {field}
              </label>
              <input
                value={formData[field as keyof typeof formData]}
                onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                placeholder={field === 'name' ? 'Jane Doe' : 'jane@email.com'}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid #3f3f46',
                  borderRadius: 8,
                  color: '#fafafa',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          ))}
        </div>
      )}
      {step === 1 && (
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fafafa', marginBottom: 16 }}>
            Service Interest
          </div>
          {[
            'Haircut & Style',
            'Color Services',
            'Treatments',
            'Bridal / Special Event',
            'Other',
          ].map((s) => (
            <button
              key={s}
              onClick={() => setFormData({ ...formData, service: s })}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 14px',
                marginBottom: 6,
                borderRadius: 8,
                border: `1px solid ${formData.service === s ? '#e879f9' : '#27272a'}`,
                background:
                  formData.service === s ? 'rgba(232,121,249,0.08)' : 'rgba(255,255,255,0.02)',
                color: formData.service === s ? '#e879f9' : '#a1a1aa',
                fontSize: 13,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.15s, border-color 0.15s, color 0.15s',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      {step === 2 && (
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fafafa', marginBottom: 16 }}>
            Your Message
          </div>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="Tell us about your vision…"
            rows={4}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid #3f3f46',
              borderRadius: 8,
              color: '#fafafa',
              fontSize: 13,
              outline: 'none',
              resize: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        {step > 0 && (
          <button
            onClick={() => setStep((prev) => prev - 1)}
            style={{
              flex: 1,
              padding: '10px 0',
              borderRadius: 8,
              border: '1px solid #3f3f46',
              background: 'transparent',
              color: '#a1a1aa',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Back
          </button>
        )}
        <button
          onClick={() => (step < totalSteps - 1 ? setStep((prev) => prev + 1) : null)}
          style={{
            flex: 1,
            padding: '10px 0',
            borderRadius: 8,
            border: 'none',
            background: '#e879f9',
            color: '#000',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {step === totalSteps - 1 ? '✓ Send Inquiry' : 'Continue →'}
        </button>
      </div>
    </div>
  )
}

function StatsCounterDemo() {
  const [triggered, setTriggered] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setTriggered(true), 300)
    return () => clearTimeout(t)
  }, [])
  const stats = [
    { value: 2437, label: 'Happy Clients', suffix: '+', color: '#a3e635', icon: '👤' },
    { value: 12, label: 'Years in Business', suffix: '', color: '#f59e0b', icon: '🏆' },
    { value: 98, label: 'Satisfaction Rate', suffix: '%', color: '#60A5FA', icon: '⭐' },
    { value: 15, label: 'Expert Stylists', suffix: '', color: '#f472b6', icon: '✂️' },
  ]
  return (
    <div style={{ width: 520, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
      {stats.map((s) => (
        <div
          key={s.label}
          style={{
            textAlign: 'center',
            padding: 16,
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid #27272a',
            borderRadius: 12,
            transition: 'transform 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)'
            e.currentTarget.style.borderColor = s.color
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'none'
            e.currentTarget.style.borderColor = '#27272a'
          }}
        >
          <div style={{ fontSize: 24, marginBottom: 8 }}>{s.icon}</div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: s.color,
              fontFamily: "'JetBrains Mono', monospace",
              marginBottom: 4,
              transition: 'color 0.6s',
            }}
          >
            {triggered ? s.value.toLocaleString() : '0'}
            {s.suffix}
          </div>
          <div
            style={{
              fontSize: 11,
              color: '#71717a',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {s.label}
          </div>
        </div>
      ))}
    </div>
  )
}

function HeroBannerDemo() {
  return (
    <div
      style={{
        width: 600,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 16,
        padding: '48px 40px',
        background: 'linear-gradient(135deg, #0f1a2a, #0a0f1a, #140f1a)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: -40,
          right: -40,
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: 'radial-gradient(circle, #22d3ee15, transparent)',
          filter: 'blur(8px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: 'radial-gradient(circle, #818cf815, transparent)',
          filter: 'blur(8px)',
        }}
      />
      <div
        style={{
          fontSize: 11,
          color: '#22d3ee',
          textTransform: 'uppercase',
          letterSpacing: '0.2em',
          fontWeight: 600,
          marginBottom: 12,
        }}
      >
        Premium Salon Experience
      </div>
      <h1
        style={{
          fontSize: 36,
          fontWeight: 800,
          color: '#fafafa',
          lineHeight: 1.2,
          marginBottom: 8,
        }}
      >
        Your Style,{' '}
        <span
          style={{
            background: 'linear-gradient(90deg, #22d3ee, #818cf8, #e879f9)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
          }}
        >
          Elevated
        </span>
      </h1>
      <p
        style={{
          fontSize: 15,
          color: '#71717a',
          maxWidth: 420,
          margin: '0 auto 24px',
          lineHeight: 1.6,
        }}
      >
        Where artistry meets innovation. Discover transformative hair experiences crafted by our
        expert team.
      </p>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
        <button
          style={{
            padding: '12px 28px',
            borderRadius: 10,
            border: 'none',
            background: '#22d3ee',
            color: '#000',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'none'
          }}
        >
          Book Now
        </button>
        <button
          style={{
            padding: '12px 28px',
            borderRadius: 10,
            border: '1px solid #3f3f46',
            background: 'transparent',
            color: '#a1a1aa',
            fontSize: 14,
            cursor: 'pointer',
            transition: 'border-color 0.2s, color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#71717a'
            e.currentTarget.style.color = '#fafafa'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#3f3f46'
            e.currentTarget.style.color = '#a1a1aa'
          }}
        >
          Our Services →
        </button>
      </div>
    </div>
  )
}

export function getDemoContent(cat: string, config: Record<string, string>): React.ReactNode {
  if (cat === 'stylist')
    return (
      <div style={{ width: 320 }}>
        <StylistFlipCard
          data={{
            name: 'Alex Rivera',
            title: 'Senior Colorist',
            description:
              "Passionate about creating personalized looks that reflect each client's unique style. Specializing in balayage, vivid colors, and precision cuts with 8+ years of experience.",
            preset: config.preset || 'default',
            bookingLink: '#',
          }}
        />
      </div>
    )
  if (cat === 'showcase' && config.type === 'gallery') return <GalleryLightboxDemo />
  if (cat === 'showcase') return <TestimonialCarouselDemo />
  if (cat === 'interactive' && config.type === 'pricing') return <PricingTableDemo />
  if (cat === 'interactive') return <BeforeAfterDemo />
  if (cat === 'booking') return <BookingWidgetDemo />
  if (cat === 'team') return <TeamRosterDemo />
  if (cat === 'services') return <ServiceMenuDemo />
  if (cat === 'loyalty') return <LoyaltyRewardsDemo />
  if (cat === 'social') return <SocialProofDemo />
  if (cat === 'info') return <HoursLocationDemo />
  if (cat === 'products') return <ProductShowcaseDemo />
  if (cat === 'timeline') return <AppointmentStepsDemo />
  if (cat === 'contact') return <ContactFormDemo />
  if (cat === 'analytics') return <StatsCounterDemo />
  if (cat === 'hero') return <HeroBannerDemo />
  return <div style={{ padding: 40, color: '#71717a', textAlign: 'center' }}>No demo available</div>
}
